import { Box, Typography } from '@/shared/ui';

import { useFlowchartImage } from '@/features/factsheet/api/queries';
import { getVersionHistoryField } from '@/features/factsheet/lib/versionHistory';
import type { FactsheetDetails } from '@/features/factsheet/types';
import Loader from '@/shared/ui/feedback/Spinner';

import RelatedCodeItem from './RelatedCodesTab/RelatedCodeItem';

import type { Sx } from '@/shared/types/uiTypes';

interface FlowchartProps {
  flowchartFileId: string;
  boxStyles?: Sx;
}

const Flowchart = ({ flowchartFileId, boxStyles }: FlowchartProps) => {
  const { data: imageSrc, isError } = useFlowchartImage(flowchartFileId);

  if (isError) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        ...boxStyles
      }}
    >
      {imageSrc ? (
        <img
          alt='flowchart'
          src={imageSrc}
          style={{ width: '90%' }}
        />
      ) : (
        <Box sx={{ width: '100%', height: 250 }}>
          <Loader />
        </Box>
      )}
    </Box>
  );
};

interface FlowchartTabProps {
  factsheet: FactsheetDetails;
  openNewTab: (code: string) => void;
}

const FlowchartTab = ({ factsheet, openNewTab }: FlowchartTabProps) => {
  const parentsWithFlowcharts = getVersionHistoryField(factsheet, 'parentsWithFlowcharts') ?? [];
  const flowchartIds = getVersionHistoryField(factsheet, 'flowcharts') ?? [];

  // Filter out invalid flowchart IDs
  const validFlowchartIds = flowchartIds.filter(id => id && id !== 'false');

  // If we have valid flowcharts, display them
  if (validFlowchartIds.length > 0) {
    return (
      <Box>
        {validFlowchartIds.map((flowchartId, index) => (
          <Flowchart
            key={flowchartId}
            boxStyles={index > 0 ? { mt: 4 } : undefined}
            flowchartFileId={flowchartId}
          />
        ))}
      </Box>
    );
  }

  // No flowcharts - show parent flowchart references
  return (
    <Box sx={{ minWidth: 700 }}>
      <Box>
        <Typography
          sx={{ pb: 1 }}
          variant='h5'
        >
          Flowchart
        </Typography>
        <Typography sx={{ pb: 2 }}>None.</Typography>
        <Typography
          sx={{ pb: 1 }}
          variant='h5'
        >
          Parent Code Flowchart
        </Typography>
        {parentsWithFlowcharts.length === 0 && <Typography sx={{ pb: 1 }}>None.</Typography>}
        {parentsWithFlowcharts.map((parentCode, index) => (
          <RelatedCodeItem
            key={`${parentCode.code}-${index}`}
            openNewTab={openNewTab}
            relatedCode={parentCode}
          />
        ))}
      </Box>
    </Box>
  );
};

export default FlowchartTab;
